/**
 * Shared geometry helpers for Site Planner viewports.
 * Module lengthwise axis = local x; 0° is horizontal (+x in world coords).
 */

export function getOrientedBoxCorners(cx, cy, length, width, angleDeg) {
    const rad = (angleDeg * Math.PI) / 180.0;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const halfL = length / 2;
    const halfW = width / 2;
    const local = [
        [-halfL, -halfW],
        [halfL, -halfW],
        [halfL, halfW],
        [-halfL, halfW],
    ];
    return local.map(([lx, ly]) => [
        cx + lx * c - ly * s,
        cy + lx * s + ly * c,
    ]);
}

export function pointInPolygon(point, polygon) {
    if (!polygon || polygon.length < 3) return false;
    const [x, y] = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        const intersect =
            (yi > y) !== (yj > y) &&
            x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0) + xi;
        if (intersect) inside = !inside;
    }
    return inside;
}

export function pointInAnyPolygon(point, polygons) {
    if (!polygons || polygons.length === 0) return true;
    return polygons.some((poly) => pointInPolygon(point, poly));
}

export function getUsablePolygons(results) {
    if (!results) return [];
    if (results.usable_boundaries && results.usable_boundaries.length > 0) {
        return results.usable_boundaries;
    }
    if (results.usable_boundary && results.usable_boundary.length >= 3) {
        return [results.usable_boundary];
    }
    return [];
}

/** Shoelace formula — unsigned polygon area in m². */
export function polygonArea(polygon) {
    if (!polygon || polygon.length < 3) return 0;
    let sum = 0;
    for (let i = 0; i < polygon.length; i++) {
        const [x1, y1] = polygon[i];
        const [x2, y2] = polygon[(i + 1) % polygon.length];
        sum += x1 * y2 - x2 * y1;
    }
    return Math.abs(sum) / 2;
}

/**
 * Build sub-area records from the setback usable polygon(s) when no splits exist yet.
 * IDs match the backend split-areas serializer (area-0, area-1, ...).
 */
export function buildUsableSubAreas(results) {
    return getUsablePolygons(results)
        .filter((boundary) => boundary && boundary.length >= 3)
        .map((boundary, idx) => ({
            id: `area-${idx}`,
            boundary,
            area_m2: polygonArea(boundary),
        }));
}

/** Sub-areas from splits, or the full usable area when nothing has been split yet. */
export function getEffectiveSubAreas(subAreas, results) {
    if (subAreas && subAreas.length > 0) return subAreas;
    return buildUsableSubAreas(results);
}

export function moduleFitsUsableArea(cx, cy, length, width, angleDeg, usablePolygons) {
    if (!usablePolygons || usablePolygons.length === 0) return true;
    const corners = getOrientedBoxCorners(cx, cy, length, width, angleDeg);
    return corners.every((pt) => pointInAnyPolygon(pt, usablePolygons));
}

export function pointerToModuleAngle(pointerX, pointerY, centroidX, centroidY) {
    const dx = pointerX - centroidX;
    const dy = pointerY - centroidY;
    const rad = Math.atan2(dy, dx);
    let deg = (rad * 180) / Math.PI;
    if (deg < 0) deg += 360;
    return Math.round(deg) % 360;
}

/** Next hatch angle step after the user places an origin point. */
export function getHatchStepAfterOrigin(existingConfig, origin) {
    const cfg = existingConfig || {};
    if (cfg.angle != null) {
        return {
            nextStep: 'direction',
            patch: { angleRef: cfg.angleRef || origin },
        };
    }
    return { nextStep: 'reference', patch: {} };
}

export function normalizeAngle360(angleDeg) {
    let a = angleDeg % 360;
    if (a < 0) a += 360;
    return a;
}

export function snapAngleWithStillThreshold(
    rawAngle,
    latchedAngle,
    pointerStillMs,
    stillThresholdMs = 1000,
    minDeltaDeg = 3,
) {
    if (pointerStillMs < stillThresholdMs) {
        return { angle: rawAngle, latched: latchedAngle };
    }
    if (latchedAngle === null) {
        return { angle: rawAngle, latched: rawAngle };
    }
    let diff = Math.abs(rawAngle - latchedAngle);
    if (diff > 180) diff = 360 - diff;
    if (diff >= minDeltaDeg) {
        return { angle: rawAngle, latched: rawAngle };
    }
    return { angle: latchedAngle, latched: latchedAngle };
}

export function getPolygonBounds(polygon) {
    if (!polygon || polygon.length === 0) {
        return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    polygon.forEach(([x, y]) => {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
    });
    return {
        minX,
        minY,
        maxX,
        maxY,
        width: maxX - minX,
        height: maxY - minY,
    };
}

export function getBoundaryEdgeLabels(polygon, minEdgeLength = 0) {
    if (!polygon || polygon.length < 2) return [];
    const labels = [];
    const n = polygon.length;
    for (let i = 0; i < n; i++) {
        const p1 = polygon[i];
        const p2 = polygon[(i + 1) % n];
        const dx = p2[0] - p1[0];
        const dy = p2[1] - p1[1];
        const len = Math.hypot(dx, dy);
        if (len < minEdgeLength) continue;
        let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
        if (angleDeg < 0) angleDeg += 360;
        const mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
        labels.push({ mid, angleDeg: Math.round(angleDeg), length: len });
    }
    return labels;
}

export function getUsableEdgeLabels(results, labelFraction = 0.05) {
    const polygons = getUsablePolygons(results);
    if (polygons.length === 0) return [];
    const bounds = getPolygonBounds(polygons[0]);
    polygons.forEach((poly) => {
        const b = getPolygonBounds(poly);
        bounds.minX = Math.min(bounds.minX, b.minX);
        bounds.minY = Math.min(bounds.minY, b.minY);
        bounds.maxX = Math.max(bounds.maxX, b.maxX);
        bounds.maxY = Math.max(bounds.maxY, b.maxY);
    });
    const ref = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
    const minEdge = ref * labelFraction;
    const labels = [];
    polygons.forEach((poly) => {
        labels.push(...getBoundaryEdgeLabels(poly, minEdge));
    });
    return labels;
}

export function pointInOrientedBox(point, cx, cy, length, width, angleDeg) {
    const corners = getOrientedBoxCorners(cx, cy, length, width, angleDeg);
    let inside = false;
    const [x, y] = point;
    for (let i = 0, j = corners.length - 1; i < corners.length; j = i++) {
        const [xi, yi] = corners[i];
        const [xj, yj] = corners[j];
        const intersect =
            (yi > y) !== (yj > y) &&
            x < ((xj - xi) * (y - yi)) / (yj - yi + 0.0) + xi;
        if (intersect) inside = !inside;
    }
    return inside;
}

export function hitTestM2Modules(point, module2List, m2Len, m2Wid) {
    if (!module2List) return -1;
    for (let i = module2List.length - 1; i >= 0; i--) {
        const m = module2List[i];
        if (pointInOrientedBox(point, m.x, m.y, m2Len, m2Wid, m.angle)) {
            return i;
        }
    }
    return -1;
}

/** Hit-test infrastructure modules (M3/M4) — same oriented-box logic as M2. */
export function hitTestInfraModules(point, moduleList, length, width) {
    if (!moduleList) return -1;
    for (let i = moduleList.length - 1; i >= 0; i--) {
        const m = moduleList[i];
        if (pointInOrientedBox(point, m.x, m.y, length, width, m.angle ?? 0)) {
            return i;
        }
    }
    return -1;
}

/** Hit-test sub-areas; returns topmost area id or null. */
export function hitTestSubAreas(point, subAreas) {
    if (!subAreas || subAreas.length === 0) return null;
    for (let i = subAreas.length - 1; i >= 0; i--) {
        const area = subAreas[i];
        if (area.boundary && pointInPolygon(point, area.boundary)) {
            return area.id;
        }
    }
    return null;
}

/**
 * Update area selection: click toggles off if sole selection;
 * shift-click adds/removes from multi-selection.
 */
export function updateAreaSelection(selectedIds, areaId, shiftKey) {
    if (!areaId) return selectedIds;
    if (shiftKey) {
        const set = new Set(selectedIds);
        if (set.has(areaId)) set.delete(areaId);
        else set.add(areaId);
        return [...set];
    }
    if (selectedIds.length === 1 && selectedIds[0] === areaId) {
        return [];
    }
    return [areaId];
}

/** Collect polygon corner and edge snap targets from boundaries. */
export function getPolygonSnapTargets(polygons) {
    const corners = [];
    const edges = [];
    if (!polygons) return { corners, edges };

    polygons.forEach((poly) => {
        if (!poly || poly.length < 2) return;
        poly.forEach((pt) => corners.push(pt));
        for (let i = 0; i < poly.length; i++) {
            const p1 = poly[i];
            const p2 = poly[(i + 1) % poly.length];
            edges.push([p1, p2]);
        }
    });
    return { corners, edges };
}

/** Distinct fill colors for sub-area overlays. */
export const SUB_AREA_COLORS = [
    'rgba(167, 139, 250, 0.12)',
    'rgba(52, 211, 153, 0.12)',
    'rgba(251, 146, 60, 0.12)',
    'rgba(96, 165, 250, 0.12)',
    'rgba(244, 114, 182, 0.12)',
];

export const SUB_AREA_STROKES = [
    '#a78bfa',
    '#34d399',
    '#fb923c',
    '#60a5fa',
    '#f472b6',
];

/** Collect all manual hatch modules from area layouts. */
export function getManualHatchModules(areaLayouts) {
    if (!areaLayouts) return [];
    const modules = [];
    Object.entries(areaLayouts).forEach(([areaId, layout]) => {
        if (layout?.module1) {
            layout.module1.forEach((m, hatchIndex) => {
                modules.push({ ...m, areaId, hatchIndex });
            });
        }
    });
    return modules;
}

/** Total manual module count across area layouts. */
export function getManualModuleCount(areaLayouts) {
    if (!areaLayouts) return 0;
    return Object.values(areaLayouts).reduce(
        (sum, layout) => sum + (layout?.module1_count || layout?.module1?.length || 0),
        0,
    );
}

export function projectPointToSegment(point, p1, p2) {
    const [px, py] = point;
    const [x1, y1] = p1;
    const [x2, y2] = p2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 1e-12) {
        return { point: p1, distance: Math.hypot(px - x1, py - y1), t: 0 };
    }
    const t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
    const tClamped = Math.max(0, Math.min(1, t));
    const projX = x1 + tClamped * dx;
    const projY = y1 + tClamped * dy;
    return {
        point: [projX, projY],
        distance: Math.hypot(px - projX, py - projY),
        t,
    };
}

export function pointToSegmentDistance(point, p1, p2) {
    return projectPointToSegment(point, p1, p2).distance;
}

/**
 * Find a boundary edge near point and return insertion index + projected point.
 * Skips hits near endpoints so corner drags keep priority.
 */
export function findBoundaryEdgeInsertion(
    point,
    boundary,
    threshold,
    endpointMargin = null,
) {
    if (!boundary || boundary.length < 2) return null;
    const margin = endpointMargin ?? threshold * 0.5;
    let best = null;
    let bestDist = threshold;
    const edgeCount = boundary.length >= 3 ? boundary.length : boundary.length - 1;

    for (let i = 0; i < edgeCount; i++) {
        const p1 = boundary[i];
        const p2 = boundary[(i + 1) % boundary.length];
        const { point: proj, distance, t } = projectPointToSegment(point, p1, p2);
        if (distance >= bestDist) continue;

        const edgeLen = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
        if (edgeLen < 1e-9) continue;
        const minT = margin / edgeLen;
        const maxT = 1 - margin / edgeLen;
        const tUse = Math.max(0, Math.min(1, t));
        if (tUse <= minT || tUse >= maxT) continue;

        bestDist = distance;
        best = { edgeIndex: i, point: proj };
    }
    return best;
}

/** Hit-test cut lines; returns closest line id within threshold (meters). */
export function hitTestCutLines(point, cutLines, threshold = 0.8) {
    if (!cutLines || cutLines.length === 0) return null;
    let bestId = null;
    let bestDist = threshold;
    for (const line of cutLines) {
        if (!line.p1 || !line.p2) continue;
        const d = pointToSegmentDistance(point, line.p1, line.p2);
        if (d < bestDist) {
            bestDist = d;
            bestId = line.id;
        }
    }
    return bestId;
}

/** Hit-test a cut line endpoint; returns 'p1', 'p2', or null. */
export function hitTestCutLineNode(point, line, threshold = 1.0) {
    if (!line?.p1 || !line?.p2) return null;
    const [px, py] = point;
    const d1 = Math.hypot(px - line.p1[0], py - line.p1[1]);
    const d2 = Math.hypot(px - line.p2[0], py - line.p2[1]);
    if (d1 < threshold && d1 <= d2) return 'p1';
    if (d2 < threshold) return 'p2';
    return null;
}

/** Dimensions for individually placed module tiles. */
export function getPlacedModuleDimensions(type, m1Params, m2Params, m3Params, m4Params) {
    if (type === 'M1') {
        return {
            length: m1Params?.length || 12.192,
            width: m1Params?.width || 2.438,
        };
    }
    if (type === 'M3') {
        return {
            length: m3Params?.length || 8.0,
            width: m3Params?.width || 3.0,
        };
    }
    if (type === 'M4') {
        return {
            length: m4Params?.length || 6.0,
            width: m4Params?.width || 2.5,
        };
    }
    return {
        length: m2Params?.length || 16.0,
        width: m2Params?.width || 2.6,
    };
}

/** Hit-test individually placed modules (topmost first). */
export function hitTestPlacedModules(point, modules, m1Params, m2Params) {
    if (!modules || modules.length === 0) return null;
    for (let i = modules.length - 1; i >= 0; i--) {
        const m = modules[i];
        const { length: len, width: wid } = getPlacedModuleDimensions(m.type, m1Params, m2Params);
        if (pointInOrientedBox(point, m.x, m.y, len, wid, m.angle)) {
            return m.id;
        }
    }
    return null;
}

/** Selection id for a manual hatch M1 module. */
export function formatHatchModuleId(areaId, index) {
    return `hatch-${areaId}-${index}`;
}

/** Selection id for an optimizer M1 module. */
export function formatResultM1ModuleId(index) {
    return `result-m1-${index}`;
}

/** Selection id for an optimizer M2 module. */
export function formatResultM2ModuleId(index) {
    return `result-m2-${index}`;
}

/** Selection id for a whole optimizer group / cluster. */
export function formatGroupSelectionId(index) {
    return `group-${index}`;
}

/** Hit-test group outlines (topmost first). */
export function hitTestGroupOutlines(point, groups) {
    if (!groups?.length || !point) return -1;
    for (let i = groups.length - 1; i >= 0; i--) {
        const outline = groups[i].outline;
        if (outline && outline.length >= 3 && pointInPolygon(point, outline)) {
            return i;
        }
    }
    return -1;
}

/** Flat module1/module2 index to the group that owns it. */
export function locateGroupModule(groups, kind, flatIndex) {
    let cursor = 0;
    for (let i = 0; i < (groups || []).length; i++) {
        const list = groups[i][kind] || [];
        if (flatIndex < cursor + list.length) {
            return { groupIndex: i, moduleIndex: flatIndex - cursor };
        }
        cursor += list.length;
    }
    return null;
}

/** Move a group's outline, halls, corridors, and eHouses by a delta. */
export function shiftGroupBy(group, dx, dy) {
    const move = (pt) => [pt[0] + dx, pt[1] + dy];
    const moveRing = (ring) => (ring || []).map(move);
    const central = group.central;
    return {
        ...group,
        center: group.center ? move(group.center) : group.center,
        outline: moveRing(group.outline),
        module1: (group.module1 || []).map((m) => ({ ...m, x: m.x + dx, y: m.y + dy })),
        module2: (group.module2 || []).map((m) => ({
            ...m,
            x: m.x + dx,
            y: m.y + dy,
            anchor_x: Number.isFinite(m.anchor_x) ? m.anchor_x + dx : m.anchor_x,
            anchor_y: Number.isFinite(m.anchor_y) ? m.anchor_y + dy : m.anchor_y,
            face_x: Number.isFinite(m.face_x) ? m.face_x + dx : m.face_x,
            face_y: Number.isFinite(m.face_y) ? m.face_y + dy : m.face_y,
        })),
        corridors: (group.corridors || []).map((corridor) => ({
            ...corridor,
            boundary: moveRing(corridor.boundary),
        })),
        central: central ? {
            ...central,
            x: Number.isFinite(central.x) ? central.x + dx : central.x,
            y: Number.isFinite(central.y) ? central.y + dy : central.y,
            boundary: moveRing(central.boundary),
        } : central,
    };
}

function _cloneGroups(groups) {
    return (groups || []).map((group) => ({
        ...group,
        center: group.center ? [...group.center] : group.center,
        outline: (group.outline || []).map((pt) => [...pt]),
        module1: (group.module1 || []).map((m) => ({ ...m })),
        module2: (group.module2 || []).map((m) => ({ ...m })),
        corridors: (group.corridors || []).map((corridor) => ({
            ...corridor,
            boundary: (corridor.boundary || []).map((pt) => [...pt]),
        })),
        central: group.central ? {
            ...group.central,
            boundary: (group.central.boundary || []).map((pt) => [...pt]),
        } : group.central,
    }));
}

/** Datahalls, corridors, and the central hall stay inside the usable area. */
export function campusRigidFits(group, dx, dy, m1Length, m1Width, usablePolygons) {
    if (!usablePolygons || usablePolygons.length === 0) return true;
    const hallsOk = (group?.module1 || []).every((mod) => moduleFitsUsableArea(
        mod.x + dx,
        mod.y + dy,
        mod.length || m1Length,
        mod.width || m1Width,
        mod.angle,
        usablePolygons,
    ));
    if (!hallsOk) return false;
    const rings = [
        ...(group?.corridors || []).map((corridor) => corridor.boundary),
        group?.central?.boundary,
    ];
    return rings.every((ring) => (
        !ring || ring.every((pt) => pointInAnyPolygon([pt[0] + dx, pt[1] + dy], usablePolygons))
    ));
}

/** Hit-test a campus by its halls, eHouses, corridors, or outline. */
export function hitTestCampusGroup(point, results, m1Params, m2Params) {
    const groups = results?.groups;
    if (!groups?.length || !point) return -1;
    const m1Len = m1Params?.length || 16;
    const m1Wid = m1Params?.width || 16;
    const m2Len = m2Params?.length || 20;
    const m2Wid = m2Params?.width || 4;
    for (let i = groups.length - 1; i >= 0; i--) {
        const group = groups[i];
        const halls = group.module1 || [];
        for (let j = halls.length - 1; j >= 0; j--) {
            const mod = halls[j];
            if (pointInOrientedBox(point, mod.x, mod.y, mod.length || m1Len, mod.width || m1Wid, mod.angle)) {
                return i;
            }
        }
        const houses = group.module2 || [];
        for (let j = houses.length - 1; j >= 0; j--) {
            const mod = houses[j];
            if (pointInOrientedBox(point, mod.x, mod.y, mod.length || m2Len, mod.width || m2Wid, mod.angle)) {
                return i;
            }
        }
        const rings = [
            ...(group.corridors || []).map((corridor) => corridor.boundary),
            group.central?.boundary,
            group.outline,
        ];
        if (rings.some((ring) => ring && ring.length >= 3 && pointInPolygon(point, ring))) {
            return i;
        }
    }
    return -1;
}

/** Rewrite flat module lists from the groups that own them. */
export function resultsWithGroupModules(results, groups) {
    const module1 = groups.flatMap((group) => group.module1 || []);
    const module2 = groups.flatMap((group) => group.module2 || []);
    const hasCampusGeom = groups.some(
        (group) => (group.corridors && group.corridors.length) || group.central,
    );
    const corridors = groups.flatMap((group) => group.corridors || []);
    const centrals = groups.map((group) => group.central).filter(Boolean);
    return {
        ...results,
        groups,
        module1,
        module2,
        module1_count: module1.length,
        module2_count: module2.length,
        group_count: groups.length,
        ...(hasCampusGeom ? {
            corridors,
            central: centrals[0] || null,
            centrals,
        } : {}),
    };
}

export function patchGroupedModule(results, kind, flatIndex, updates) {
    if (!results) return results;
    if (results.groups?.length) {
        const groups = _cloneGroups(results.groups);
        const loc = locateGroupModule(groups, kind, flatIndex);
        if (!loc) return results;
        const list = groups[loc.groupIndex][kind];
        list[loc.moduleIndex] = { ...list[loc.moduleIndex], ...updates };
        return resultsWithGroupModules(results, groups);
    }
    const list = [...(results[kind] || [])];
    if (!list[flatIndex]) return results;
    list[flatIndex] = { ...list[flatIndex], ...updates };
    return { ...results, [kind]: list, [`${kind}_count`]: list.length };
}

export function removeGroupedModule(results, kind, flatIndex) {
    if (!results) return results;
    if (results.groups?.length) {
        const groups = _cloneGroups(results.groups);
        const loc = locateGroupModule(groups, kind, flatIndex);
        if (!loc) return results;
        groups[loc.groupIndex][kind].splice(loc.moduleIndex, 1);
        const kept = groups.filter(
            (group) => (group.module1?.length || 0) + (group.module2?.length || 0) > 0,
        );
        return resultsWithGroupModules(results, kept);
    }
    const list = [...(results[kind] || [])];
    if (!list[flatIndex]) return results;
    list.splice(flatIndex, 1);
    return { ...results, [kind]: list, [`${kind}_count`]: list.length };
}

export function removeGroupAt(results, groupIndex) {
    if (!results?.groups?.[groupIndex]) return results;
    const groups = results.groups.filter((_, index) => index !== groupIndex);
    return resultsWithGroupModules(results, groups);
}

export function translateGrouped(results, groupIndex, dx, dy, snapshot) {
    if (!results?.groups?.[groupIndex] || !snapshot) return results;
    const groups = results.groups.map((group, index) => (
        index === groupIndex ? shiftGroupBy(snapshot, dx, dy) : group
    ));
    return resultsWithGroupModules(results, groups);
}

export function translateAllGroups(results, dx, dy, snapshots) {
    if (!results || !snapshots?.length) return results;
    const groups = snapshots.map((group) => shiftGroupBy(group, dx, dy));
    return resultsWithGroupModules(results, groups);
}

/** Center of a cluster, using each group's stored center. */
export function groupClusterCentroid(groups) {
    const points = (groups || []).map((group) => group.center).filter((pt) => pt && pt.length >= 2);
    if (!points.length) return null;
    const x = points.reduce((sum, pt) => sum + pt[0], 0) / points.length;
    const y = points.reduce((sum, pt) => sum + pt[1], 0) / points.length;
    return [x, y];
}

export function replaceGroupedModule2(results, updatedM2) {
    if (!results) return results;
    const next = updatedM2 || [];
    if (!results.groups?.length) {
        return { ...results, module2: next, module2_count: next.length };
    }
    let cursor = 0;
    const groups = results.groups.map((group) => {
        const count = (group.module2 || []).length;
        const module2 = next.slice(cursor, cursor + count).map((mod) => ({ ...mod }));
        cursor += count;
        return { ...group, module2 };
    });
    const rebuilt = resultsWithGroupModules(results, groups);
    if (cursor < next.length) {
        const module2 = [...rebuilt.module2, ...next.slice(cursor).map((mod) => ({ ...mod }))];
        return { ...rebuilt, module2, module2_count: module2.length };
    }
    return rebuilt;
}

/** Parse a module selection id into its source parts. */
export function parseModuleSelectionId(id) {
    if (!id) return null;
    if (id.startsWith('hatch-')) {
        const rest = id.slice('hatch-'.length);
        const sep = rest.lastIndexOf('-');
        if (sep <= 0) return null;
        const index = Number(rest.slice(sep + 1));
        if (!Number.isInteger(index)) return null;
        return { type: 'hatch', areaId: rest.slice(0, sep), index };
    }
    if (id.startsWith('result-m1-')) {
        const index = Number(id.slice('result-m1-'.length));
        if (!Number.isInteger(index)) return null;
        return { type: 'resultM1', index };
    }
    if (id.startsWith('result-m2-')) {
        const index = Number(id.slice('result-m2-'.length));
        if (!Number.isInteger(index)) return null;
        return { type: 'resultM2', index };
    }
    if (id.startsWith('group-')) {
        const index = Number(id.slice('group-'.length));
        if (!Number.isInteger(index)) return null;
        return { type: 'group', index };
    }
    return { type: 'placed', id };
}

/** Hit-test optimizer M1 modules (topmost first). */
export function hitTestResultM1Modules(point, results, m1Params) {
    const module1 = results?.module1;
    if (!module1 || module1.length === 0) return -1;
    const m1Len = m1Params?.length || 12.192;
    const m1Wid = m1Params?.width || 2.438;
    for (let i = module1.length - 1; i >= 0; i--) {
        const m = module1[i];
        if (pointInOrientedBox(point, m.x, m.y, m1Len, m1Wid, m.angle)) {
            return i;
        }
    }
    return -1;
}

/** Hit-test hatch modules from area layouts (topmost first). */
export function hitTestHatchModules(point, areaLayouts, m1Params) {
    if (!areaLayouts) return null;
    const m1Len = m1Params?.length || 12.192;
    const m1Wid = m1Params?.width || 2.438;
    const entries = [];
    Object.entries(areaLayouts).forEach(([areaId, layout]) => {
        (layout?.module1 || []).forEach((m, index) => {
            entries.push({ areaId, index, m });
        });
    });
    for (let i = entries.length - 1; i >= 0; i--) {
        const { areaId, index, m } = entries[i];
        if (pointInOrientedBox(point, m.x, m.y, m1Len, m1Wid, m.angle)) {
            return { areaId, index };
        }
    }
    return null;
}

/**
 * Snap hatch/module angle to boundary edge directions within tolerance.
 * Module lengthwise axis aligns with edge angle or perpendicular (+90°).
 */
export function snapAngleToEdges(angleDeg, edgeAngles, toleranceDeg = 2) {
    if (!edgeAngles || edgeAngles.length === 0) return angleDeg;
    const normalized = normalizeAngle360(angleDeg);
    let best = normalized;
    let bestDiff = toleranceDeg + 1;

    edgeAngles.forEach((edgeAngle) => {
        const candidates = [
            edgeAngle,
            (edgeAngle + 90) % 360,
            (edgeAngle + 180) % 360,
            (edgeAngle + 270) % 360,
        ];
        candidates.forEach((candidate) => {
            let diff = Math.abs(normalized - candidate);
            if (diff > 180) diff = 360 - diff;
            if (diff <= toleranceDeg && diff < bestDiff) {
                bestDiff = diff;
                best = candidate;
            }
        });
    });

    return bestDiff <= toleranceDeg ? best : normalized;
}

/** Collect unique edge angles from boundary and sub-areas for angle snapping. */
export function getSnapEdgeAngles(boundary, subAreas, results) {
    const angles = new Set();
    const addPoly = (poly) => {
        getBoundaryEdgeLabels(poly).forEach(({ angleDeg }) => angles.add(angleDeg));
    };
    if (boundary?.length >= 3) addPoly(boundary);
    (subAreas || []).forEach((area) => {
        if (area.boundary) addPoly(area.boundary);
    });
    const usable = getUsablePolygons(results);
    usable.forEach((poly) => addPoly(poly));
    return [...angles];
}

/** Format sub-area display label (A1, A2, ...). */
export function formatAreaLabel(index) {
    return `A${index + 1}`;
}

export function getAreaLabel(areaId, subAreas) {
    const idx = (subAreas || []).findIndex((a) => a.id === areaId);
    return idx >= 0 ? formatAreaLabel(idx) : areaId;
}

export function getPolygonCentroid(polygon) {
    if (!polygon || polygon.length === 0) return [0, 0];
    let sx = 0;
    let sy = 0;
    polygon.forEach(([x, y]) => {
        sx += x;
        sy += y;
    });
    return [sx / polygon.length, sy / polygon.length];
}

/** True when point lies on any polygon edge within tolerance (meters). */
export function pointOnPolygonBorder(point, polygons, tolerance = 0.8) {
    if (!point || !polygons?.length) return false;
    for (const poly of polygons) {
        if (!poly || poly.length < 2) continue;
        const edgeCount = poly.length >= 3 ? poly.length : poly.length - 1;
        for (let i = 0; i < edgeCount; i++) {
            const p1 = poly[i];
            const p2 = poly[(i + 1) % poly.length];
            if (pointToSegmentDistance(point, p1, p2) <= tolerance) {
                return true;
            }
        }
    }
    return false;
}

export function pointsNearEachOther(a, b, tolerance = 0.8) {
    if (!a || !b) return false;
    return Math.hypot(a[0] - b[0], a[1] - b[1]) <= tolerance;
}

/** Snap hit or polygon edge counts as a division-line border endpoint. */
export function isDivisionBorderPoint(point, snapInfo, borderPolygons, tolerance = 0.8) {
    if (snapInfo?.type === 'corner' || snapInfo?.type === 'edge') {
        return true;
    }
    return pointOnPolygonBorder(point, borderPolygons, tolerance);
}

/** Consecutive point pairs for an open division polyline. */
export function divisionPolylineToSegments(points) {
    const segments = [];
    if (!points || points.length < 2) return segments;
    for (let i = 0; i < points.length - 1; i++) {
        segments.push([points[i], points[i + 1]]);
    }
    return segments;
}

function _unitVector(p0, p1) {
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) return null;
    return [dx / len, dy / len];
}

function _leftNormal(dir) {
    return [-dir[1], dir[0]];
}

function _lineIntersection(p0, d0, p1, d1) {
    const cross = d0[0] * d1[1] - d0[1] * d1[0];
    if (Math.abs(cross) < 1e-8) return null;
    const dx = p1[0] - p0[0];
    const dy = p1[1] - p0[1];
    const t = (dx * d1[1] - dy * d1[0]) / cross;
    return [p0[0] + t * d0[0], p0[1] + t * d0[1]];
}

function _offsetPointAtVertex(vertex, dir, halfWidth, side) {
    const sign = side === 'left' ? 1 : -1;
    const n = _leftNormal(dir);
    return [
        vertex[0] + n[0] * sign * halfWidth,
        vertex[1] + n[1] * sign * halfWidth,
    ];
}

/** Offset polyline with miter joins at centerline vertices. */
export function buildCorridorOffsetPolyline(centerline, halfWidth, side) {
    const n = centerline.length;
    if (n < 2 || !(halfWidth > 0)) return [];

    const dirs = [];
    for (let i = 0; i < n - 1; i++) {
        const dir = _unitVector(centerline[i], centerline[i + 1]);
        if (!dir) return [];
        dirs.push(dir);
    }

    const points = [];
    points.push(_offsetPointAtVertex(centerline[0], dirs[0], halfWidth, side));

    for (let i = 1; i < n - 1; i++) {
        const d0 = dirs[i - 1];
        const d1 = dirs[i];
        const p0 = _offsetPointAtVertex(centerline[i], d0, halfWidth, side);
        const p1 = _offsetPointAtVertex(centerline[i], d1, halfWidth, side);
        const turn = d0[0] * d1[1] - d0[1] * d1[0];
        const outside = (side === 'left' && turn > 1e-8) || (side === 'right' && turn < -1e-8);
        const miter = outside ? _lineIntersection(p0, d0, p1, d1) : null;
        if (miter) {
            const dist = Math.hypot(
                miter[0] - centerline[i][0],
                miter[1] - centerline[i][1],
            );
            if (dist <= halfWidth * 4) {
                points.push(miter);
                continue;
            }
        }
        points.push(p0, p1);
    }

    points.push(_offsetPointAtVertex(centerline[n - 1], dirs[n - 2], halfWidth, side));
    return points;
}

function _polylineToSegments(points) {
    const segments = [];
    for (let i = 0; i < points.length - 1; i++) {
        segments.push([points[i], points[i + 1]]);
    }
    return segments;
}

/**
 * Build miter-joined corridor wall segments for display (finite chords between corners).
 * Does not include infinite extensions past bend intersections.
 */
export function computeCorridorDisplayLines(
    centerline,
    width,
    borderPolygons,
    borderTolerance = 0.8,
) {
    if (!centerline || centerline.length < 2 || !(width > 0)) return [];

    const halfW = width / 2;
    const left = buildCorridorOffsetPolyline(centerline, halfW, 'left');
    const right = buildCorridorOffsetPolyline(centerline, halfW, 'right');
    if (!left.length || !right.length) return [];

    const lines = [
        ..._polylineToSegments(left),
        ..._polylineToSegments(right),
    ];

    const start = centerline[0];
    const end = centerline[centerline.length - 1];
    if (!pointOnPolygonBorder(start, borderPolygons, borderTolerance)) {
        lines.push([left[0], right[0]]);
    }
    if (!pointOnPolygonBorder(end, borderPolygons, borderTolerance)) {
        lines.push([left[left.length - 1], right[right.length - 1]]);
    }

    return lines;
}

/** Build viewport/DXF wall segments for committed corridors. */
export function buildCorridorWallLines(corridors, borderPolygons) {
    const lines = [];
    (corridors || []).forEach((corridor) => {
        if (!corridor?.centerline || corridor.centerline.length < 2) return;
        const segments = computeCorridorDisplayLines(
            corridor.centerline,
            corridor.width,
            borderPolygons,
        );
        segments.forEach((seg, index) => {
            lines.push({
                id: `${corridor.id}-wall-${index}`,
                p1: seg[0],
                p2: seg[1],
                source: 'corridor',
            });
        });
    });
    return lines;
}

/** @deprecated alias */
export function computeCorridorCutLines(
    centerline,
    width,
    borderPolygons,
    borderTolerance = 0.8,
) {
    return computeCorridorDisplayLines(centerline, width, borderPolygons, borderTolerance);
}

/** Oriented box corner polygons to exclude from cursor snap during module placement. */
export function getModuleSnapExcludePolygons(
    placedModules,
    areaLayouts,
    results,
    m1Params,
    m2Params,
) {
    const polys = [];
    const addBox = (cx, cy, length, width, angle) => {
        polys.push(getOrientedBoxCorners(cx, cy, length, width, angle));
    };

    (placedModules || []).forEach((m) => {
        const dims = getPlacedModuleDimensions(m.type, m1Params, m2Params);
        addBox(m.x, m.y, dims.length, dims.width, m.angle);
    });

    getManualHatchModules(areaLayouts).forEach((m) => {
        addBox(m.x, m.y, m1Params?.length || 12.192, m1Params?.width || 2.438, m.angle);
    });

    (results?.module1 || []).forEach((m) => {
        addBox(m.x, m.y, m1Params?.length || 12.192, m1Params?.width || 2.438, m.angle);
    });

    (results?.module2 || []).forEach((m) => {
        addBox(m.x, m.y, m2Params?.length || 16.0, m2Params?.width || 2.6, m.angle);
    });

    return polys;
}
